/**
 * O casco do app: link de pulo, cabecalho, conteudo e a navegacao de uma mao.
 *
 * ## Decisoes de acessibilidade que sao requisito, nao acabamento (WCAG 2.2 AA)
 *
 * - **Link de pulo** (2.4.1). Sem ele, quem navega por teclado atravessa a barra inferior
 *   inteira a cada rota. Ele e o primeiro elemento focavel do documento e so aparece no foco.
 * - **Uma landmark de cada** (1.3.1): um `<header>`, um `<main id="conteudo">`, um `<nav>` com
 *   `aria-label`. O `axe` reprova conteudo fora de landmark, e a landmark e responsabilidade da
 *   PAGINA — por isso o teste de tela roda com a regra `region` LIGADA, ao contrario do teste
 *   de componente do `@farmacia/ui`.
 * - **`aria-current="page"`** no item ativo da navegacao (4.1.2): "selecionado" precisa chegar
 *   ao leitor de tela, e nao so a cor.
 * - **Alvo de toque de 48 px** (2.5.8 e DESIGN_SYSTEM §3): a barra inferior e o lugar onde o
 *   toque errado acontece, porque e onde o polegar chega.
 * - **`padding-bottom` com `env(safe-area-inset-bottom)`**: no iPhone, a barra de gestos come o
 *   ultimo item da navegacao.
 *
 * ## CLS zero comeca aqui
 *
 * A barra inferior tem altura fixa declarada em CSS, e o `<main>` reserva o espaco dela com
 * `padding-bottom`. Sem isso, o conteudo "pula" quando a barra monta — e o orcamento de CLS do
 * `PWA §11.2` e 0,05 na Home e 0,01 no checkout.
 */

import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { texto } from '../../conteudo/index.js';
import { useMinhaLoja } from '../../loja/contexto.jsx';
import { BotaoDeLoja, FolhaDeEscolhaDeLoja } from '../../loja/SeletorDeLoja.jsx';
import { AvisoDeAtualizacao } from './AvisoDeAtualizacao.jsx';
import { BarraDeConexao } from './BarraDeConexao.jsx';

export interface ItemDeNavegacao {
  readonly para: string;
  readonly rotulo: string;
  /** Quantidade no carrinho, por exemplo. `undefined` nao renderiza nada. */
  readonly contador?: number;
}

const NAVEGACAO: readonly ItemDeNavegacao[] = [
  { para: '/', rotulo: texto('app.inicio') },
  { para: '/busca', rotulo: texto('app.busca') },
  { para: '/carrinho', rotulo: texto('app.carrinho') },
  { para: '/pedidos', rotulo: texto('app.pedidos') },
  { para: '/conta', rotulo: texto('app.conta') },
];

export interface ShellProps {
  readonly children: ReactNode;
  /** Quantidade de itens no carrinho, para o contador da navegacao. */
  readonly itensNoCarrinho?: number;
}

export function Shell({ children, itensNoCarrinho }: ShellProps): ReactNode {
  const caminho = useRouterState({ select: (estado) => estado.location.pathname });
  const { seletorAberto } = useMinhaLoja();

  return (
    <div className="app-shell">
      {/* Primeiro elemento focavel do documento (WCAG 2.4.1). */}
      <a className="app-shell__pular" href="#conteudo">
        {texto('app.pularParaConteudo')}
      </a>

      <BarraDeConexao />
      <AvisoDeAtualizacao />

      {/*
        `DESIGN_SYSTEM §7.2` e `PWA §10.2`: a filial vive no cabecalho, e "trocar de loja" fica
        **sempre visivel**. Uma landmark `<header>` so no documento (1.3.1); o `aria-label`
        existe porque um `<header>` sem nome, num app com `<nav>` e `<main>` rotulados, e o
        unico marco que o leitor de tela anuncia sem dizer do que se trata.
      */}
      <header className="cabecalho-loja" aria-label={texto('loja.cabecalho.rotulo')}>
        <BotaoDeLoja />
      </header>

      {/*
        A folha SO monta quando abre: e ela quem chama `GET /v1/stores`, e um casco que a
        montasse sempre poria uma consulta de rede em toda tela do app.
      */}
      {seletorAberto ? <FolhaDeEscolhaDeLoja /> : null}

      <main id="conteudo" className="app-shell__conteudo" tabIndex={-1}>
        {children}
      </main>

      <nav className="app-nav" aria-label={texto('app.navegacaoPrincipal')}>
        <ul className="app-nav__lista">
          {NAVEGACAO.map((item) => {
            const ativo = item.para === '/' ? caminho === '/' : caminho.startsWith(item.para);
            const contador = item.para === '/carrinho' ? itensNoCarrinho : undefined;
            return (
              <li key={item.para} className="app-nav__item">
                <Link
                  to={item.para}
                  className="app-nav__link"
                  // `aria-current` e o que diz "voce esta aqui" a quem nao ve a cor.
                  {...(ativo ? { 'aria-current': 'page' as const } : {})}
                  // `PWA §11.5` — prefetch em `pointerdown`/`touchstart`, nunca em hover nem
                  // por visibilidade: adivinhar navegacao gasta a franquia de quem paga por MB.
                  preload="intent"
                >
                  <span className="app-nav__rotulo">{item.rotulo}</span>
                  {contador !== undefined && contador > 0 ? (
                    <span className="app-nav__contador" aria-hidden="true">
                      {contador}
                    </span>
                  ) : null}
                  {contador !== undefined && contador > 0 ? (
                    // O contador visual e `aria-hidden`; o numero chega ao leitor de tela como
                    // texto completo, senao ele leria "Carrinho 3" sem dizer 3 o que.
                    <span className="balcao-sr-only">{`, ${String(contador)} item(ns)`}</span>
                  ) : null}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
